import {
  Checkbox,
  Chip,
  CircularProgress,
  DialogContent,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  CenterFocusStrong as CenterFocusStrongIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  DragIndicator as DragIndicatorIcon,
  Layers as LayersIcon,
  Refresh as RefreshIcon,
  Room as RoomIcon,
  Tune as TuneIcon,
} from "@material-ui/icons";
import { Reorder, useDragControls } from "framer-motion";
import L from "leaflet";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  GeoJSON,
  MapContainer,
  Polyline,
  TileLayer,
  useMap,
  Marker,
  useMapEvents,
} from "react-leaflet";
import { alignLng, geoJsonToLines, unwrapLines, WorldCopies } from "../../../../util/worldCopies";
import { v4 as uuidv4 } from "uuid";
import * as actions from "../actions";
import { toastError } from "../../../../utils";
import { ZoneLayer, ZONES } from "../../../../util/zoneOverlays";

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: 360,
    "& .MuiDrawer-paper": {
      width: 360,
      backgroundColor: "#ffffff",
    },
  },
  drawerHeader: {
    backgroundColor: "#1a2332",
    color: "#ffffff",
    padding: theme.spacing(1.5, 2),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  formControl: {
    width: "100%",
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  chip: {
    margin: "3px 4px",
    fontSize: "11px",
    fontWeight: 500,
    borderRadius: "16px",
  },
}));

const CHANNELS = [
  { id: "suez", name: "Suez Canal" },
  { id: "panama", name: "Panama Canal" },
  { id: "gibraltar", name: "Strait of Gibraltar" },
  { id: "singapore", name: "Strait of Singapore" },
  { id: "dover", name: "Strait of Dover" },
  { id: "kiel", name: "Kiel Canal" },
  { id: "bosphorus", name: "Bosphorus" },
  { id: "messina", name: "Strait of Messina" },
  { id: "corinth", name: "Corinth Canal" },
  { id: "floridaStrait", name: "Florida Strait" },
  { id: "magellan", name: "Strait of Magellan" },
  { id: "oresund", name: "Oresund" },
];

function ResetCenterView(props) {
  const { selectPosition } = props;
  const map = useMap();

  useEffect(() => {
    if (selectPosition) {
      map.setView(
        L.latLng(selectPosition?.lat, selectPosition?.lon),
        map.getZoom(),
        { animate: true }
      );
    }
  }, [selectPosition, map]);

  return null;
}

const markersvg = (num, portName = "", country = "", isFirst = false, isLast = false) => {
  const pinColor = isFirst ? "#1e40af" : isLast ? "#065f46" : "#1f2937";
  const accentColor = isFirst ? "#3b82f6" : isLast ? "#10b981" : "#64748b";
  const roleLabel = isFirst ? "START" : isLast ? "END" : `WP ${num}`;
  const roleClass = isFirst ? "is-start" : isLast ? "is-end" : "is-waypoint";
  const displayName = portName || `Waypoint ${num}`;

  return `
    <div class="maritime-marker-root ${roleClass}">
      <div class="maritime-sonar-emitter">
        <div class="sonar-wave wave-1" style="border-color: ${accentColor};"></div>
        <div class="sonar-wave wave-2" style="border-color: ${accentColor};"></div>
        <div class="sonar-beacon-dot" style="background: ${accentColor};"></div>
      </div>

      <div class="maritime-pin-label-card">
        <span class="pin-role-tag ${roleClass}">${roleLabel}</span>
        <span class="pin-port-title" title="${displayName}">${displayName}</span>
        ${country ? `<span class="pin-country-badge">${country}</span>` : ""}
      </div>

      <div class="maritime-pin-stem">
        <svg
          width="28"
          height="38"
          viewBox="0 0 60 81"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="maritime-pin-vector"
        >
          <path
            d="M30.0336 80.2488C29.0354 80.2488 28.124 79.8148 27.473 79.0336C21.0496 71.0043 0 43.7918 0 29.9902C0 13.4544 13.4544 0 29.9902 0C46.5261 0 59.9804 13.4544 59.9804 29.9902C59.9804 43.8352 38.9743 71.0477 32.5075 79.0336C31.9433 79.8148 31.0318 80.2488 30.0336 80.2488ZM30.0336 6.72718C17.2303 6.72718 6.81398 17.1435 6.81398 29.9468C6.81398 42.7502 17.2303 53.1664 30.0336 53.1664C42.837 53.1664 53.2533 42.7502 53.2533 29.9468C53.2533 17.1435 42.837 6.72718 30.0336 6.72718Z"
            fill="${pinColor}"
          />
          <path
            d="M30.0318 49.2165C40.6744 49.2165 49.302 40.589 49.302 29.9464C49.302 19.3038 40.6744 10.6763 30.0318 10.6763C19.3892 10.6763 10.7617 19.3038 10.7617 29.9464C10.7617 40.589 19.3892 49.2165 30.0318 49.2165Z"
            fill="white"
          />
          <circle cx="30" cy="30" r="15" fill="${accentColor}" fill-opacity="0.15" />
          <text
            x="30"
            y="32"
            fill="${pinColor}"
            font-weight="600"
            text-anchor="middle"
            dominant-baseline="central"
            font-family="Inter, -apple-system, BlinkMacSystemFont, sans-serif"
            font-size="20"
          >
            ${num}
          </text>
        </svg>
      </div>
    </div>
  `;
};

// Subcomponent for each draggable waypoint row using framer-motion Reorder
function WaypointTimelineRow({ loc, idx, totalCount, leg, onRemove }) {
  const dragControls = useDragControls();
  const isFirst = idx === 0;
  const isLast = idx === totalCount - 1 && totalCount > 1;
  const legNormal = leg?.total_length ? Number(leg.total_length).toFixed(1) : "0.0";
  const legEca = leg?.seca_length ? Number(leg.seca_length).toFixed(1) : "0.0";

  return (
    <Reorder.Item
      as="div"
      value={loc}
      key={loc.id}
      dragListener={false}
      dragControls={dragControls}
      className="port-timeline-row"
      whileDrag={{ scale: 1.02, zIndex: 50, boxShadow: "0 8px 20px rgba(0,0,0,0.12)" }}
    >
      {/* Timeline Rail */}
      <div className="timeline-rail">
        <div className={`node-dot ${isFirst ? "start" : isLast ? "end" : "waypoint"}`} />
        {!isLast && <div className="rail-connector" />}
      </div>

      {/* Port Item Content */}
      <div className="port-item-content">
        <div className="port-box-pill" title={loc.placeName}>
          {/* Drag Handle */}
          <div
            className="waypoint-drag-handle"
            onPointerDown={(e) => dragControls.start(e)}
            title="Drag to reorder waypoint"
          >
            <DragIndicatorIcon style={{ fontSize: 14 }} />
          </div>

          {loc.flagUrl ? (
            <img
              src={loc.flagUrl}
              alt=""
              className="w-4 h-3 rounded-sm flex-shrink-0 object-cover"
            />
          ) : (
            <span className="country-flag-badge">{loc.countryCode || "WP"}</span>
          )}

          <div className="flex-1 min-w-0 flex flex-col">
            <span className="port-title-text">
              {idx + 1}. {loc.placeName}
            </span>
            <span className="text-[9.5px] font-mono text-slate-400">
              {loc.lat
                ? `${Math.abs(Number(loc.lat)).toFixed(2)}°${Number(loc.lat) >= 0 ? "N" : "S"}, `
                : ""}
              {loc.lng
                ? `${Math.abs(Number(loc.lng)).toFixed(2)}°${Number(loc.lng) >= 0 ? "E" : "W"}`
                : ""}
            </span>
          </div>

          <button
            type="button"
            className="port-box-remove"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(loc.id);
            }}
            title="Remove waypoint"
          >
            <CloseIcon style={{ fontSize: 13 }} />
          </button>
        </div>

        {!isLast && (
          <div className="leg-distance-metric">
            {leg && Number(leg.total_length) > 0 ? (
              <span>
                Normal <strong>{legNormal} nm</strong> · ECA <strong>{legEca} nm</strong>
              </span>
            ) : (
              <span className="italic text-slate-400">Pending calculation</span>
            )}
          </div>
        )}
      </div>
    </Reorder.Item>
  );
}

export default function CustomDistanceMap(props) {
  const { setLoader = () => {}, topTabs } = props;
  const classes = useStyles();

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [mapInstance, setMapInstance] = useState(null);

  const [ecaValue, setECAValue] = useState("ignore");
  const [hraValue, setHRAValue] = useState("ignore");
  const [jwcValue, setJWCValue] = useState("ignore");
  const [show, setShow] = useState(false);
  const [showRoute, setShowRoute] = useState(false);
  const [selectPosition] = useState(null);

  const [distance, setDistance] = useState(0);
  const [seca_length, setSeca_length] = useState(0);
  const [getAllDisctance, setAllDisctance] = useState([]);
  const [isCalculating, setIsCalculating] = useState(false);

  const [points, setPoints] = useState([]);
  const [counter, setCounter] = useState(0);
  const [locationList, setLocationList] = useState([]);
  const locationListRef = useRef(locationList);
  const legCacheRef = useRef({});

  useEffect(() => {
    locationListRef.current = locationList;
  }, [locationList]);

  const [isCheckboxChecked, setIsCheckboxChecked] = useState(true);
  const [isHraChecked, setIsHraChecked] = useState(false);
  const [isJwcChecked, setIsJwcChecked] = useState(false);
  const [rerenderKey, setRerenderKey] = useState(0);
  const [geoJsonData, setGeoJsonData] = useState(null);
  // Route unwrapped across the antimeridian so it can repeat on every world copy
  const routeLines = useMemo(
    () => (geoJsonData ? unwrapLines(geoJsonToLines([geoJsonData])) : []),
    [geoJsonData]
  );
  // Waypoint lngs as one continuous chain, aligned to the route when there is one
  const waypointLngs = useMemo(() => {
    const chain = unwrapLines([locationList.map((p) => [p.lng, p.lat])])[0] || [];
    return chain.map(([x]) => (routeLines.length ? alignLng(x, routeLines) : x));
  }, [locationList, routeLines]);

  const [chipStatus, setChipStatus] = useState({
    suez: true,
    panama: true,
    corinth: true,
    kiel: true,
    gibraltar: true,
    messina: true,
    singapore: true,
    dover: true,
    magellan: true,
    floridaStrait: true,
    bosphorus: true,
    oresund: true,
  });

  const handleECAChange = (event) => setECAValue(event.target.value);
  const handleHRAChange = (event) => setHRAValue(event.target.value);
  const handleJWCChange = (event) => setJWCValue(event.target.value);

  const toggleChipStatus = (chipName) => {
    setChipStatus((prevStatus) => ({
      ...prevStatus,
      [chipName]: !prevStatus[chipName],
    }));
  };

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const handleCloseRoute = () => setShowRoute(false);
  const handleShowRoute = () => setShowRoute(true);

  const handleCheckboxChange = () => setIsCheckboxChecked(!isCheckboxChecked);
  const handleHraChange = () => setIsHraChecked(!isHraChecked);
  const handleJwcChange = () => setIsJwcChecked(!isJwcChecked);

  const identifyOcean = (lat, lon) => {
    const atlantic = { latMin: -30, latMax: 70, lonMin: -100, lonMax: -10 };
    const pacific = { latMin: -30, latMax: 70, lonMin: -180, lonMax: -70 };
    const indian = { latMin: -30, latMax: 30, lonMin: 40, lonMax: 100 };
    const arctic = { latMin: 70, latMax: 90, lonMin: -180, lonMax: 180 };

    if (lat >= atlantic.latMin && lat <= atlantic.latMax && lon >= atlantic.lonMin && lon <= atlantic.lonMax) {
      return "Atlantic Ocean";
    } else if (lat >= pacific.latMin && lat <= pacific.latMax && lon >= pacific.lonMin && lon <= pacific.lonMax) {
      return "Pacific Ocean";
    } else if (lat >= indian.latMin && lat <= indian.latMax && lon >= indian.lonMin && lon <= indian.lonMax) {
      return "Indian Ocean";
    } else if (lat >= arctic.latMin && lat <= arctic.latMax && lon >= arctic.lonMin && lon <= arctic.lonMax) {
      return "Arctic Ocean";
    }
    return "Sea Waypoint";
  };

  const getPlaceName = async (lat, lon, waypointId) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`
      );
      const data = await response.json();
      let placeName = "Unknown";
      let flagUrl = "";
      let countryCode = "";

      if (data && data.address) {
        if (data.address.country) {
          placeName = data.address.country;
          if (data.address.country_code) {
            countryCode = data.address.country_code.toUpperCase();
            flagUrl = `https://flagcdn.com/w20/${data.address.country_code.toLowerCase()}.png`;
          }
        }
      } else {
        placeName = identifyOcean(lat, lon);
      }

      setLocationList((prevList) => {
        const updated = prevList.map((item) => {
          if (item.id === waypointId) {
            return {
              ...item,
              placeName,
              flagUrl,
              countryCode,
              lat: data?.lat ? Number(data.lat) : lat,
              lng: data?.lon ? Number(data.lon) : lon,
            };
          }
          return item;
        });
        locationListRef.current = updated;

        return updated;
      });
    } catch (err) {
      const oceanName = identifyOcean(lat, lon);
      setLocationList((prevList) => {
        const updated = prevList.map((item) => {
          if (item.id === waypointId) {
            return {
              ...item,
              placeName: oceanName,
              countryCode: "SEA",
            };
          }
          return item;
        });
        locationListRef.current = updated;
        return updated;
      });
    }
  };

  const calculateFullRoute = async (listToCalculate = locationListRef.current) => {
    if (!listToCalculate || listToCalculate.length < 2) {
      setGeoJsonData(null);
      setDistance(0);
      setSeca_length(0);
      setAllDisctance([]);
      setRerenderKey((k) => k + 1);
      return;
    }

    try {
      setIsCalculating(true);
      setLoader(true);

      const legPromises = [];
      const failedLegIndices = [];

      for (let i = 0; i < listToCalculate.length - 1; i++) {
        const fromP = listToCalculate[i];
        const toP = listToCalculate[i + 1];
        const fromLat = Number(fromP.lat);
        const fromLng = Number(fromP.lng);
        const toLat = Number(toP.lat);
        const toLng = Number(toP.lng);

        const cacheKey = `${fromLat.toFixed(3)},${fromLng.toFixed(3)}_${toLat.toFixed(3)},${toLng.toFixed(3)}_${chipStatus.suez}_${chipStatus.panama}_${ecaValue}_${hraValue}_${jwcValue}`;

        if (legCacheRef.current[cacheKey]) {
          // Instantly reuse previously calculated leg
          legPromises.push(Promise.resolve({ success: true, data: legCacheRef.current[cacheKey], index: i }));
        } else {
          const requestBody = {
            request: {
              from: { lng: fromLng, lat: fromLat },
              to: { lng: toLng, lat: toLat },
              options: {
                eca: ecaValue || "ignore",
                hra: hraValue || "ignore",
                jwc: jwcValue || "ignore",
                nogoBlocks: [],
                autovalidate: "true",
                suez: chipStatus.suez.toString(),
                panama: chipStatus.panama.toString(),
                corinth: chipStatus.corinth.toString(),
                kiel: chipStatus.kiel.toString(),
                gibraltar: chipStatus.gibraltar.toString(),
                messina: chipStatus.messina.toString(),
                singapore: chipStatus.singapore.toString(),
                dover: chipStatus.dover.toString(),
                magellan: chipStatus.magellan.toString(),
                floridaStrait: chipStatus.floridaStrait.toString(),
                bosphorus: chipStatus.bosphorus.toString(),
                oresund: chipStatus.oresund.toString(),
                northeast: "false",
                northwest: "false",
              },
            },
          };

          legPromises.push(
            actions
              .getCustomDistance(requestBody)
              .then((res) => {
                if (res?.data?.features?.[0]) {
                  legCacheRef.current[cacheKey] = res.data;
                }
                return { success: true, data: res?.data, index: i };
              })
              .catch((err) => {
                failedLegIndices.push(i + 1);
                return { success: false, error: err, index: i };
              })
          );
        }
      }

      const results = await Promise.all(legPromises);

      if (failedLegIndices.length > 0) {
        toastError(
          `Unable to calculate sea route for leg ${failedLegIndices.join(", ")}. Please ensure waypoints are in navigable water and canals are permitted.`
        );
      }

      let totalDist = 0;
      let totalSeca = 0;
      const legDistances = [];
      const allCoordinates = [];
      let baseFeature = null;

      results.forEach((res, i) => {
        if (res.success && res.data?.features?.[0]) {
          const feat = res.data.features[0];
          if (!baseFeature) {
            baseFeature = feat;
          }
          const legLength = feat.properties?.total_length || 0;
          const legSeca = feat.properties?.seca_length || 0;
          totalDist += legLength;
          totalSeca += legSeca;
          legDistances.push({
            total_length: legLength,
            seca_length: legSeca,
          });
          if (feat.geometry?.coordinates) {
            allCoordinates.push(...feat.geometry.coordinates);
          }
        } else {
          legDistances.push({
            total_length: 0,
            seca_length: 0,
          });
          allCoordinates.push(
            [Number(listToCalculate[i].lng), Number(listToCalculate[i].lat)],
            [Number(listToCalculate[i + 1].lng), Number(listToCalculate[i + 1].lat)]
          );
        }
      });

      if (allCoordinates.length > 0) {
        setGeoJsonData({
          type: "Feature",
          properties: {
            ...(baseFeature ? baseFeature.properties : {}),
            total_length: totalDist,
            seca_length: totalSeca,
          },
          geometry: {
            type: "LineString",
            coordinates: allCoordinates,
          },
        });
      }

      setDistance(totalDist);
      setSeca_length(totalSeca);
      setAllDisctance(legDistances);
      setRerenderKey((k) => k + 1);
    } catch (error) {
      console.error("Error calculating custom distance:", error);
    } finally {
      setIsCalculating(false);
      setLoader(false);
    }
  };

  const handleReorderWaypoints = (newOrder) => {
    setLocationList(newOrder);
    locationListRef.current = newOrder;
    setPoints(newOrder.map((loc) => ({ lat: loc.lat, lng: loc.lng })));

    // No auto calculation on reorder - clear previous route so user clicks Calculate
    setGeoJsonData(null);
    setDistance(0);
    setSeca_length(0);
    setAllDisctance([]);
    setRerenderKey((k) => k + 1);
  };

  const handleRemoveWaypoint = (targetId) => {
    const currentList = locationListRef.current;
    const itemToRemove = currentList.find((item) => item.id === targetId);
    const remaining = currentList.filter((item) => item.id !== targetId);
    setLocationList(remaining);
    locationListRef.current = remaining;
    setPoints(remaining.map((p) => ({ lat: p.lat, lng: p.lng })));

    // No auto calculation on remove - clear previous route so user clicks Calculate
    setGeoJsonData(null);
    setDistance(0);
    setSeca_length(0);
    setAllDisctance([]);
    setRerenderKey((k) => k + 1);
  };

  const handleResetRoute = () => {
    setLocationList([]);
    locationListRef.current = [];
    setPoints([]);
    setCounter(0);
    setGeoJsonData(null);
    setDistance(0);
    setSeca_length(0);
    setAllDisctance([]);
    setRerenderKey((k) => k + 1);
  };

  const handleFitRouteBounds = () => {
    if (!mapInstance || points.length === 0) return;
    const bounds = L.latLngBounds(points);
    mapInstance.fitBounds(bounds, { padding: [60, 60], maxZoom: 12 });
  };

  const MapController = ({ onMapReady }) => {
    const map = useMap();
    useEffect(() => {
      if (map) {
        setMapInstance(map);
        if (onMapReady) onMapReady(map);
      }
    }, [map]);
    return null;
  };

  const MapWithLayers = () => {
    const map = useMap();

    useEffect(() => {
      if (!map._layersControl) {
        const baseMaps = {
          "ArcGIS Street": L.tileLayer(
            "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
          ),
          "Sea Profile": L.tileLayer(
            "https://server.arcgisonline.com/ArcGIS/rest/services/Ocean/World_Ocean_Base/MapServer/tile/{z}/{y}/{x}"
          ),
          Imagery: L.tileLayer(
            "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          ),
          "Gray Canvas": L.tileLayer(
            "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}"
          ),
          "Carto Light": L.tileLayer(
            "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png"
          ),
        };

        const layersControl = L.control.layers(baseMaps).addTo(map);
        map.zoomControl.setPosition("topright");
        map._layersControl = layersControl;
      }
    }, [map]);

    return null;
  };

  const MapEvents = () => {
    const map = useMapEvents({
      click: (e) => {
        // Clicks on a repeated world copy give lng outside ±180; store the real coordinate
        const { lat, lng } = e.latlng.wrap();
        const newId = uuidv4();
        const currentList = locationListRef.current;
        const nextNum = currentList.length + 1;

        // Pins are rendered declaratively on every world copy (see WorldCopies below)
        const newWaypoint = {
          id: newId,
          lat,
          lng,
          placeName: `WP ${nextNum}`,
          countryCode: "",
          flagUrl: "",
        };

        const nextList = [...currentList, newWaypoint];
        setLocationList(nextList);
        locationListRef.current = nextList;
        setPoints(nextList.map((p) => ({ lat: p.lat, lng: p.lng })));
        setCounter(nextNum);

        getPlaceName(lat, lng, newId);

        // No auto calculation on adding waypoints - clear previous route so user clicks Calculate
        if (distance > 0) {
          setGeoJsonData(null);
          setDistance(0);
          setSeca_length(0);
          setAllDisctance([]);
          setRerenderKey((k) => k + 1);
        }
      },
    });

    return null;
  };

  const totalDurationDays = distance > 0 ? distance / (13.0 * 24) : 0;
  const ecaPercent = distance > 0 ? ((seca_length / distance) * 100).toFixed(1) : "0.0";
  const nonEcaDist = Math.max(0, distance - seca_length);
  const nonEcaPercent = distance > 0 ? ((nonEcaDist / distance) * 100).toFixed(1) : "0.0";

  return (
    <div className="w-full h-full flex flex-row overflow-hidden bg-white">
      {/* Left-Hand Sidebar (Signal Ocean Style) */}
      <div
        className={`signal-ocean-panel ${isSidebarCollapsed ? "collapsed" : ""}`}
        onClick={isSidebarCollapsed ? () => setIsSidebarCollapsed(false) : undefined}
        title={isSidebarCollapsed ? "Expand sidebar" : undefined}
      >
        {/* Panel Header */}
        <div
          className="panel-header"
          onClick={isSidebarCollapsed ? () => setIsSidebarCollapsed(false) : undefined}
        >
          {!isSidebarCollapsed && (
            <div className="flex items-center gap-2 overflow-hidden">
              <span
                className="font-medium text-[11px] uppercase tracking-wider whitespace-nowrap"
                style={{ color: "#475569" }}
              >
                Custom Route
              </span>
              {locationList.length > 0 && (
                <span
                  className="text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap inline-flex items-center leading-none"
                  style={{ background: "#f3f4f6", color: "#475569", border: "1px solid #d1d5db" }}
                >
                  {locationList.length} Waypoints
                </span>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsSidebarCollapsed(!isSidebarCollapsed);
            }}
            className={isSidebarCollapsed ? "panel-toggle-collapsed" : "panel-toggle-btn"}
            title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isSidebarCollapsed ? (
              <ChevronRightIcon style={{ fontSize: 18 }} />
            ) : (
              <>
                <span className="toggle-label">Hide</span>
                <ChevronLeftIcon className="toggle-icon" style={{ fontSize: 14 }} />
              </>
            )}
          </button>
        </div>

        {isSidebarCollapsed ? (
          <div
            className="flex-1 flex items-center justify-center cursor-pointer transition-colors py-4"
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "")}
            onClick={() => setIsSidebarCollapsed(false)}
            title="Expand Route Summary"
          >
            <span
              style={{
                writingMode: "vertical-rl",
                transform: "rotate(180deg)",
                fontSize: "11px",
                letterSpacing: "0.1em",
                fontWeight: 500,
                color: "#6b7280",
                textTransform: "uppercase",
                userSelect: "none",
              }}
            >
              Custom Route
            </span>
          </div>
        ) : (
          <>
            <div className="panel-body">
              {/* Instruction banner - only shown when 0 or 1 point is selected */}
              {locationList.length <= 1 && (
                <div
                  className="p-2.5 rounded-md text-[11px] font-medium flex items-start gap-2"
                  style={{ background: "#f8fafc", border: "1px solid #e2e8f0", color: "#64748b" }}
                >
                  <RoomIcon style={{ fontSize: 15, color: "#3b82f6", flexShrink: 0, marginTop: 1 }} />
                  <div className="leading-snug">
                    Click on the map to add custom sea waypoints, then click Calculate to find the optimal route.
                  </div>
                </div>
              )}

              {/* Waypoints List Card with Framer-Motion Drag and Drop */}
              <div className="voyage-port-locations-card">
                <div className="card-header-row">
                  <div className="card-header-left">
                    <RoomIcon style={{ fontSize: 13, color: "#64748b" }} />
                    <span>WAYPOINTS</span>
                  </div>
                  <span className="card-ports-badge">{locationList.length} Points</span>
                </div>

                <div className="port-locations-scroll-area">
                  {locationList.length > 0 ? (
                    <Reorder.Group
                      as="div"
                      axis="y"
                      values={locationList}
                      onReorder={handleReorderWaypoints}
                      className="port-timeline-track"
                    >
                      {locationList.map((loc, idx) => (
                        <WaypointTimelineRow
                          key={loc.id}
                          loc={loc}
                          idx={idx}
                          totalCount={locationList.length}
                          leg={getAllDisctance?.[idx]}
                          onRemove={handleRemoveWaypoint}
                        />
                      ))}
                    </Reorder.Group>
                  ) : (
                    <div className="text-[11px] italic py-6 text-slate-400 font-medium text-center">
                      No waypoints placed yet.<br />Click anywhere on the map to begin.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Pinned Bottom Options Section */}
            <div className="panel-footer">
              <div
                className="text-[11px] font-medium tracking-wider uppercase"
                style={{ color: "#475569" }}
              >
                OPTIONS & SETTINGS
              </div>

              {/* Drawers Action Buttons */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="signal-toggle-pill flex-1 justify-center"
                  onClick={handleShowRoute}
                  title="Routing & Channel Settings"
                >
                  <TuneIcon style={{ fontSize: 13 }} />
                  <span>Routing</span>
                </button>
                <button
                  type="button"
                  className="signal-toggle-pill flex-1 justify-center"
                  onClick={handleShow}
                  title="Map Layer Overlays"
                >
                  <LayersIcon style={{ fontSize: 13 }} />
                  <span>Map Layers</span>
                </button>
              </div>

              {/* Primary Action Buttons: Calculate and Reset */}
              <div className="pt-1 flex items-center gap-2">
                <button
                  type="button"
                  className="flex-1 h-8 py-1 px-3 rounded-md text-white font-medium text-[11px] tracking-wide transition-all flex items-center justify-center gap-1.5 disabled:cursor-not-allowed"
                  style={{
                    background: locationList.length < 2 ? "#94a3b8" : "#1a2332",
                  }}
                  onMouseEnter={(e) => {
                    if (!e.currentTarget.disabled) e.currentTarget.style.background = "#111827";
                  }}
                  onMouseLeave={(e) => {
                    if (!e.currentTarget.disabled) e.currentTarget.style.background = "#1a2332";
                  }}
                  onClick={() => calculateFullRoute(locationList)}
                  disabled={locationList.length < 2 || isCalculating}
                  title={
                    locationList.length < 2
                      ? "Add at least 2 waypoints on the map to calculate"
                      : "Calculate optimal maritime route"
                  }
                >
                  {isCalculating ? (
                    <>
                      <CircularProgress color="inherit" size={12} />
                      <span>Calculating...</span>
                    </>
                  ) : (
                    <span>Calculate</span>
                  )}
                </button>

                {locationList.length > 0 && (
                  <button
                    type="button"
                    className="h-8 py-1 px-3 rounded-md text-[11px] font-medium transition-colors"
                    style={{ border: "1.5px solid #d1d5db", color: "#374151", background: "#ffffff" }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#f9fafb";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#ffffff";
                    }}
                    onClick={handleResetRoute}
                    title="Clear all waypoints and reset"
                  >
                    Reset Route
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Map Column */}
      <div className="flex-1 h-full flex flex-col overflow-hidden relative z-0">
        {topTabs}
        <div className="flex-1 relative w-full h-full overflow-hidden">
          <MapContainer
          center={[20, 0]}
          zoom={3}
          minZoom={2}
          style={{ height: "100%", width: "100%" }}
        >
          <MapController />
          <MapEvents />

          {/* Clean ArcGIS World Street Map as default */}
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
            attribution="&copy; Esri, HERE, Garmin, FAO, NOAA, USGS, OpenStreetMap"
          />
          <MapWithLayers />

          {/* Waypoints, reference line and route, repeated on every world copy */}
          {waypointLngs.length > 0 && (
            <WorldCopies lngs={[...waypointLngs, ...routeLines.flat().map(([x]) => x)]}>
              {(offset) => {
                const data = {
                  type: "MultiLineString",
                  coordinates: routeLines.map((line) => line.map(([x, y]) => [x + offset, y])),
                };
                return (
            <>
              {locationList.map((loc, idx) => (
                <Marker
                  key={loc.id}
                  position={[Number(loc.lat), waypointLngs[idx] + offset]}
                  icon={L.divIcon({
                    html: markersvg(
                      idx + 1,
                      loc.placeName,
                      loc.countryCode,
                      idx === 0,
                      idx === locationList.length - 1 && locationList.length > 1
                    ),
                    className: "maritime-custom-div-icon",
                    iconSize: [0, 0],
                    iconAnchor: [0, 0],
                    popupAnchor: [0, -42],
                  })}
                />
              ))}

              {/* Waypoint reference polyline */}
              <Polyline
                positions={locationList.map((loc, idx) => [Number(loc.lat), waypointLngs[idx] + offset])}
                color="#94a3b8"
                weight={1.5}
                dashArray="5, 8"
                opacity={0.5}
              />

              {/* Calculated Nautical Route Corridor (3-layer multi-tier animated corridor) */}
              {routeLines.length > 0 && (
              <>
              {/* Layer 1: Wide Nautical Corridor Glow */}
              <GeoJSON
                key={`glow-${rerenderKey}`}
                data={data}
                style={{
                  color: "#38bdf8",
                  weight: 9,
                  opacity: 0.28,
                  className: "maritime-corridor-glow",
                  lineCap: "round",
                  lineJoin: "round",
                }}
              />

              {/* Layer 2: Base High-Contrast Navigation Track */}
              <GeoJSON
                key={`base-${rerenderKey}`}
                data={data}
                style={{
                  color: "#1e3a8a",
                  weight: 3.5,
                  opacity: 0.9,
                  className: "maritime-route-base",
                  lineCap: "round",
                  lineJoin: "round",
                }}
              />

              {/* Layer 3: Dynamic Animated Vessel Transit Flow */}
              <GeoJSON
                key={`flow-${rerenderKey}`}
                data={data}
                style={{
                  color: "#93c5fd",
                  weight: 2.5,
                  opacity: 0.95,
                  dashArray: "8, 14",
                  className: "maritime-route-animated-flow",
                  lineCap: "round",
                  lineJoin: "round",
                }}
              />
              </>
              )}
            </>
                );
              }}
            </WorldCopies>
          )}

          <ResetCenterView selectPosition={selectPosition} />

          {/* Regulatory Overlays */}
          {isCheckboxChecked && <ZoneLayer zone={ZONES[0]} />}
          {isHraChecked && <ZoneLayer zone={ZONES[1]} />}
          {isJwcChecked && <ZoneLayer zone={ZONES[2]} />}
        </MapContainer>

        {/* Floating Top-Left Route Metrics Row (HUD) */}
        <div className="maritime-floating-metrics-row">
          {/* 1. Duration */}
          <div className="metric-pill-col">
            <span className="metric-pill-label">DURATION</span>
            <div className="metric-pill-body">
              <span className="metric-val-strong">
                {totalDurationDays > 0 ? totalDurationDays.toFixed(1) : "0"}
                <span className="unit">d</span>
              </span>
              <span className="metric-sub-detail">
                {totalDurationDays > 0
                  ? `${Math.floor(totalDurationDays)}d ${Math.round((totalDurationDays % 1) * 24)}h`
                  : "At 13.0 kts"}
              </span>
            </div>
          </div>

          <div className="metric-pill-divider" />

          {/* 2. Distance */}
          <div className="metric-pill-col">
            <span className="metric-pill-label">DISTANCE</span>
            <div className="metric-pill-body">
              <span className="metric-val-strong">
                {Math.round(distance).toLocaleString()}
                <span className="unit">NM</span>
              </span>
              <span className="metric-sub-detail">Total distance</span>
            </div>
          </div>

          <div className="metric-pill-divider" />

          {/* 3. (S)ECA */}
          <div className="metric-pill-col">
            <span className="metric-pill-label">
              <span className="metric-dot eca" />
              (S)ECA
            </span>
            <div className="metric-pill-body">
              <span className="metric-val-strong">
                {Math.round(seca_length).toLocaleString()}
                <span className="unit">NM</span>
              </span>
              <span className="metric-sub-detail">
                {distance > 0 ? `${ecaPercent}%` : "0%"}
              </span>
            </div>
          </div>

          <div className="metric-pill-divider" />

          {/* 4. Non-ECA */}
          <div className="metric-pill-col">
            <span className="metric-pill-label">
              <span className="metric-dot noneca" />
              NON-ECA
            </span>
            <div className="metric-pill-body">
              <span className="metric-val-strong">
                {Math.round(nonEcaDist).toLocaleString()}
                <span className="unit">NM</span>
              </span>
              <span className="metric-sub-detail">
                {distance > 0 ? `${nonEcaPercent}%` : "0%"}
              </span>
            </div>
          </div>
        </div>

        {/* Floating Quick Action: Fit Route */}
        {points.length > 0 && (
          <div className="maritime-map-floating-actions">
            <button
              type="button"
              className="map-recenter-btn"
              onClick={handleFitRouteBounds}
              title="Fit entire custom route on screen"
            >
              <CenterFocusStrongIcon style={{ fontSize: 15 }} />
              <span>Fit Route</span>
            </button>
          </div>
        )}
      </div>
      </div>

      {/* Drawer: Routing Settings */}
      <Drawer
        anchor="right"
        open={showRoute}
        onClose={handleCloseRoute}
        className={classes.drawer}
      >
        <div className={classes.drawerHeader}>
          <div className="flex items-center gap-2">
            <TuneIcon style={{ fontSize: 16 }} />
            <span className="text-[13px] font-medium tracking-wide">Routing Settings</span>
          </div>
          <IconButton onClick={handleCloseRoute} size="small" style={{ color: "#ffffff" }}>
            <CloseIcon style={{ fontSize: 16 }} />
          </IconButton>
        </div>
        <DialogContent className="p-4">
          <FormControl className={classes.formControl} size="small">
            <InputLabel style={{ fontSize: "12px" }}>ECA Areas</InputLabel>
            <Select value={ecaValue} onChange={handleECAChange} style={{ fontSize: "12px" }}>
              <MenuItem value="minimize">Minimize</MenuItem>
              <MenuItem value="ignore">Ignore</MenuItem>
            </Select>
          </FormControl>

          <FormControl className={classes.formControl} size="small">
            <InputLabel style={{ fontSize: "12px" }}>HRA Areas</InputLabel>
            <Select value={hraValue} onChange={handleHRAChange} style={{ fontSize: "12px" }}>
              <MenuItem value="minimize">Minimize</MenuItem>
              <MenuItem value="ignore">Ignore</MenuItem>
            </Select>
          </FormControl>

          <FormControl className={classes.formControl} size="small">
            <InputLabel style={{ fontSize: "12px" }}>Joint War Committee Areas</InputLabel>
            <Select value={jwcValue} onChange={handleJWCChange} style={{ fontSize: "12px" }}>
              <MenuItem value="minimize">Minimize</MenuItem>
              <MenuItem value="ignore">Ignore</MenuItem>
            </Select>
          </FormControl>

          <div className="mt-4 mb-2">
            <span className="text-[11px] font-medium text-slate-600 uppercase tracking-wider">
              Canals & Straits
            </span>
          </div>
          <div className="flex flex-wrap gap-1">
            {CHANNELS.map((ch) => (
              <Chip
                key={ch.id}
                label={ch.name}
                onClick={() => toggleChipStatus(ch.id)}
                className={classes.chip}
                variant={chipStatus[ch.id] ? "default" : "outlined"}
                style={{
                  backgroundColor: chipStatus[ch.id] ? "#eff6ff" : "#ffffff",
                  borderColor: chipStatus[ch.id] ? "#3b82f6" : "#cbd5e1",
                  color: chipStatus[ch.id] ? "#1d4ed8" : "#64748b",
                }}
              />
            ))}
          </div>

          <div className="mt-6 pt-3 border-t border-slate-200 flex justify-end gap-2">
            <button
              type="button"
              className="px-3 py-1.5 rounded-md text-[11px] font-medium text-white transition-colors"
              style={{ background: "#1a2332" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#111827")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#1a2332")}
              onClick={handleCloseRoute}
            >
              Apply Settings
            </button>
          </div>
        </DialogContent>
      </Drawer>

      {/* Drawer: Map Overlays */}
      <Drawer
        anchor="right"
        open={show}
        onClose={handleClose}
        className={classes.drawer}
      >
        <div className={classes.drawerHeader}>
          <div className="flex items-center gap-2">
            <LayersIcon style={{ fontSize: 16 }} />
            <span className="text-[13px] font-medium tracking-wide">Map Layers</span>
          </div>
          <IconButton onClick={handleClose} size="small" style={{ color: "#ffffff" }}>
            <CloseIcon style={{ fontSize: 16 }} />
          </IconButton>
        </div>
        <DialogContent className="p-4">
          <div className="mb-2">
            <span className="text-[11px] font-medium text-slate-600 uppercase tracking-wider">
              Zones & Areas of Interest
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <FormControlLabel
              control={
                <Checkbox
                  checked={isCheckboxChecked}
                  onChange={handleCheckboxChange}
                  name="ecaAreas"
                  color="primary"
                  size="small"
                />
              }
              label={<span className="text-[12px] text-slate-700">Emission Control Areas (ECA)</span>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isHraChecked}
                  onChange={handleHraChange}
                  name="hraAreas"
                  color="primary"
                  size="small"
                />
              }
              label={<span className="text-[12px] text-slate-700">High Risk Areas (HRA)</span>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isJwcChecked}
                  onChange={handleJwcChange}
                  name="jwcAreas"
                  color="primary"
                  size="small"
                />
              }
              label={<span className="text-[12px] text-slate-700">Joint War Committee (JWC)</span>}
            />
          </div>
        </DialogContent>
      </Drawer>
    </div>
  );
}